import { TrainingVideoDialog } from '../dialogs/training-video-dialog/training-video-dialog';
import { VendorSignatureDialog } from '../dialogs/vendor-signature-dialog/vendor-signature-dialog';
import { TrainingVideoService } from './training-video.service';
import { Resource, Vendor, VendorResourceUpdateRequest } from 'common';
import { Inject, Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, forkJoin, of, throwError, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Event, NavigationEnd, Router } from '@angular/router';
import { OptometristService } from './oz-api.service';
import { MatDialog } from '@angular/material/dialog';
import { isNullOrUndefiend } from '@ng-shared';
import { DOCUMENT } from '@angular/common';

const MOCK_VENDOR: Vendor = {
    address: 'FAKE_VALUE',
    administrationFee: 4321,
    agents: [],
    email: 'FAKE_VALUE',
    id: 1234,
    initiationFee: 1,
    name: 'FAKE_VALUE',
    postalCode: 'FAKE_VALUE',
    registrationNumber: 'FAKE_VALUE',
    resources: [],
    telephone: 'FAKE_VALUE',
    tradingName: 'FAKE_VALUE',
}

@Injectable({
    providedIn: 'root'
})
export class VendorService implements OnDestroy {

    public get currentVendor(): Vendor | undefined {
        return this.currentVendor$.value;
    }

    public get vendorSignature(): Resource | undefined {
        return this.currentVendor?.resources.find(r => r.resourceType === 15);
    }

    public currentVendor$: BehaviorSubject<Vendor | undefined> = new BehaviorSubject<Vendor | undefined>(undefined);

    /**
     * SSR-safe reference to the local storage.
     */
    private localStorageRef: Storage | undefined = this.document.defaultView?.localStorage;

    /**
     * The subscription to track router events.
     */
    private sub: Subscription | undefined = undefined;

    private hasBeenPrompted: boolean = false;

    private hasVideoBeenPrompted: boolean = false;

    private readonly apiService: OptometristService = inject(OptometristService);

    private readonly router: Router = inject(Router);

    private readonly dialog: MatDialog = inject(MatDialog);

    private readonly trainingVideoService: TrainingVideoService = inject(TrainingVideoService);

    constructor(@Inject(DOCUMENT) private readonly document: Document) {
        // Clear data when navigating to /login or /logout.
        this.sub = this.router.events
            .subscribe((event: Event) => {
                if (event instanceof NavigationEnd && (this.router.url.includes('/login') || this.router.url.includes('/logout'))) {
                    this.currentVendor$.next(undefined);
                }
            });
    }

    /**
     * Loads the vendor if it is not set.
     *
     * @return {(Promise<Vendor | undefined>)} The vendor.
     */
    public async loadVendorIfNotSet(): Promise<Vendor | undefined> {
        if (this.currentVendor) {
            return this.currentVendor;
        }

        if (USE_MOCKS) {
            return MOCK_VENDOR;
        }

        // Get the vendor ID from the previous login response.
        // If this fails, the user is not logged in and the auth guard would've redirected them.
        let vendorId: number | undefined = JSON.parse(this.localStorageRef?.getItem('previousLoginResponse') || '{}')?.vendorId;

        if (isNullOrUndefiend(vendorId)) {
            // Sleep 1s and try again.
            await new Promise(resolve => setTimeout(resolve, 1000));

            vendorId = JSON.parse(this.localStorageRef?.getItem('previousLoginResponse') || '{}')?.vendorId;
        }

        if (!isNullOrUndefiend(vendorId)) {
            const vendorFromAPI: Vendor | undefined = await firstValueFrom(this.apiService.searchFindVendor(vendorId as number));

            this.currentVendor$.next(vendorFromAPI);

            const requestSignature: boolean = !vendorFromAPI?.resources?.find((r: Resource) => r.resourceType === 15);
            if (requestSignature) {
                this.requestVendorSignature();
            } else {
                // No signature needed, check if training video should be shown
                this.showTrainingVideoIfNeeded();
            }

            return this.currentVendor;
        }
        else {
            throw new Error('Vendor ID not available.');
            // TODO: maybe issue a redirect to login here?
        }

    }

    public initializeAppData(): Observable<boolean> {
        if (USE_MOCKS) {
            this.currentVendor$.next(MOCK_VENDOR);
            this._triggerPostLoadDialogs();
            return of(true);
        }

        const vendorId: number | undefined =
            JSON.parse(this.localStorageRef?.getItem('previousLoginResponse') || '{}')?.vendorId;

        if (vendorId == null) {
            return throwError(() => new Error('Vendor ID not found in localStorage.'));
        }

        return forkJoin({
            vendor: this.apiService.searchFindVendor(vendorId),
        }).pipe(
            tap(({ vendor }) => {
                this.currentVendor$.next(vendor);
                this._triggerPostLoadDialogs();
            }),
            map(() => true),
        );
    }

    private _triggerPostLoadDialogs(): void {
        const requestSignature = !this.currentVendor?.resources?.find(
            (r: Resource) => r.resourceType === 15
        );
        if (requestSignature) {
            this.requestVendorSignature();
        } else {
            this.showTrainingVideoIfNeeded();
        }
    }

    private requestVendorSignature(): void {
        if (!this.hasBeenPrompted) {
            this.hasBeenPrompted = true;
            // Prompt the user to update their signature.
            const dialogRef = this.dialog.open(VendorSignatureDialog);

            // After the signature dialog closes, check if training video should be shown
            dialogRef.afterClosed().subscribe(() => {
                this.showTrainingVideoIfNeeded();
            });
        }
    }

    private showTrainingVideoIfNeeded(): void {
        if (!this.hasVideoBeenPrompted && this.trainingVideoService.shouldShowTrainingVideo()) {
            this.hasVideoBeenPrompted = true;
            this.dialog.open(TrainingVideoDialog, {
                width: '90vw',
                maxWidth: '450px',
            });
        }
    }

    public async updateVendorSignature(body: VendorResourceUpdateRequest): Promise<void> {
        if (!this.currentVendor) {
            await this.loadVendorIfNotSet();
        }

        if (!this.currentVendor) {
            throw new Error('Vendor not loaded.');
        }

        return firstValueFrom(this.apiService.vendorResourceSave(body));
    }

    public ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }
}
